
import React from 'react';
import { Helmet } from 'react-helmet-async';
import SEOHead, { SEOProps } from './SEOHead';
import StructuredData from './StructuredData';

type PageType = 'Article' | 'Product' | 'Service' | 'BreadcrumbList' | 'Organization' | 'WebSite' | 'FAQPage' | 'Course' | 'MedicalWebPage' | 'HowTo';

export interface PageSEOProps extends SEOProps {
  pageName: string;
  pageType?: PageType;
  structuredData?: Record<string, any>;
  children?: React.ReactNode;
  datePublished?: string;
  dateModified?: string;
  breadcrumbs?: {
    position: number;
    name: string;
    item: string;
  }[];
}

const PageSEO = ({
  title,
  description,
  canonicalUrl,
  ogImage,
  ogType,
  keywords,
  noindex,
  pageName,
  pageType = 'WebSite',
  structuredData,
  children,
  datePublished,
  dateModified,
  breadcrumbs,
  ...rest
}: PageSEOProps) => {
  // Generate page-specific structured data
  const pageStructuredData = {
    "@context": "https://schema.org",
    "@type": pageType,
    name: title,
    description: description,
    url: canonicalUrl,
    ...(datePublished && { datePublished }),
    ...(dateModified && { dateModified }),
    ...(structuredData || {})
  };
  
  // Generate breadcrumb structured data if breadcrumbs are provided
  const breadcrumbData = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map(crumb => ({
      "@type": "ListItem",
      "position": crumb.position,
      "name": crumb.name,
      "item": crumb.item
    }))
  } : null;

  return (
    <>
      <SEOHead
        title={title}
        description={description}
        canonicalUrl={canonicalUrl}
        ogImage={ogImage}
        ogType={ogType}
        keywords={keywords}
        noindex={noindex}
        {...rest}
      />
      
      <StructuredData type={pageType} data={pageStructuredData} />
      
      {breadcrumbData && (
        <StructuredData type="BreadcrumbList" data={breadcrumbData} />
      )}
      
      {children}
    </>
  );
};

export default PageSEO;
